import { Card, Col, Container, Row, Stack } from "react-bootstrap";
import PropTypes from "prop-types";
import { CTA, Form, Phone, SocialShare, Tag, Title } from "@/ui";
import Calendar from "@/assets/Calendar.svg";
import Contract from "@/assets/Contract.svg";
import Location from "@/assets/Location.svg";
import Salary from "@/assets/Salary.svg";
import classes from "./styles.module.scss";
import clsx from "classnames";
import { getGlobal } from "@/getters/getGlobal";
import { formatDistanceToNowStrict } from "date-fns";
import { RelatedJobs } from "@/components";
import { jobs_helper } from "@/helpers/jobs_helper";
import { branch_helper } from "@/helpers/branch_helper";
import { consultant_helper } from "@/helpers/consultant_helper";
import { employer_helper } from "@/helpers/employer_helper";
import { candidate_testimonies_helper } from "@/helpers/candidate_testimonies_helper";
import { stripTags } from "@/functions/stripTags";
import { formatSalary } from "@/functions/formatSalary";
import { StarRating } from "@/ui/StarRating";

export default function JobPageContent({
  className,
  id,
  description,
  img,
  categories,
  location,
  salary_package,
  external_reference,
  published_at,
  related,
  external_application_email,
  url_slug,
}) {
  const job_types = jobs_helper.getCategoryValues("275d8990-bd9e-4f79-a0e2-d81bb734c855", { categories });
  const sectors = jobs_helper.getCategoryValues("3186657c-e89c-4a6f-9157-35eb7fe0b379", { categories });
  const branches = jobs_helper.getCategoryValues("33b21b47-15e3-40a2-bc7b-32019e94c7ba", { categories });
  const employers = jobs_helper.getCategoryValues("1df83e15-03f4-4ce7-9f8d-9b20d0ea1538", { categories });
  const branch = branch_helper.find(branches[0]?.id, "id");
  const employer = employer_helper.find(employers[0]?.id, "id");
  const testimony = candidate_testimonies_helper.random();

  const nameFragment = /^(.*)\.\d+\.\d/.exec(external_application_email)[1];
  const sanitisedNameFragment = nameFragment.split(".").join("").toLowerCase();

  let consultant = consultant_helper.filter(
    (c) => c.name.toLowerCase().replace(/\s+/g, "") == sanitisedNameFragment,
  )[0];

  if (!consultant) {
    consultant = consultant_helper.find((i) => {
      const nameparts = i["name"].split(" ");
      const shortname = `${nameparts[0][0]}${nameparts.slice(1).join("")}`.toLowerCase();

      return shortname == sanitisedNameFragment;
    });
  }

  const global = getGlobal();

  return (
    <div className={clsx(className, classes.content)}>
      <Container className="mw-xxl">
        <Row>
          <Col xs={12} md={8}>
            <CTA
              className={clsx(classes.content__details__cta, 'd-md-none mb-3')}
              label="Apply now"
              href="#Apply"
              variant="secondary"
            />
            <Card className={clsx(classes.content__details, "mb-5")}>
              <Card.Header>
                <h3>Job Details</h3>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="p-4 border-bottom">
                  <Row>
                    <Col xs={12} md={5}>
                      {published_at &&
                        <dl>
                          <dt>
                            <span className="visually-hidden">Published</span>
                            <Calendar />
                          </dt>
                          <dd>
                            <time>{`Posted ${formatDistanceToNowStrict(new Date(published_at))} ago`}</time>
                          </dd>
                        </dl>
                      }
                      {location &&
                        <dl>
                          <dt>
                            <span className="visually-hidden">Location</span>
                            <Location width={16} height={20} />
                          </dt>
                          <dd>{location}</dd>
                        </dl>
                      }
                    </Col>
                    <Col xs={12} md={5}>
                      {job_types.length > 0 &&
                        <dl>
                          <dt>
                            <span className="visually-hidden">Role Type</span>
                            <Contract />
                          </dt>
                          <dd>{job_types.map((i) => i.name).join(", ")}</dd>
                        </dl>
                      }
                      {salary_package &&
                        <dl>
                          <dt>
                            <span className="visually-hidden">Salary</span>
                            <Salary />
                          </dt>
                          <dd>{formatSalary(salary_package)}</dd>
                        </dl>
                      }
                    </Col>
                    <Col xs={12} md={2}>
                      {img && <img className="ms-auto d-block" src={img} alt="" />}
                    </Col>
                  </Row>
                  <Stack className="flex-row flex-wrap flex-md-nowrap mb-3" gap={2}>
                    {sectors.map(({ name }, k) => (
                      <Tag key={k} label={name} variant="primary" />
                    ))}
                  </Stack>
                  <div>
                    <b>Job Ref: </b>
                    {external_reference}
                  </div>
                </div>
                <div className="p-4" dangerouslySetInnerHTML={{ __html: description }} />
                <a id="Apply" />
                <div className="bg-light p-4">
                  <Title title="Apply now" />
                  <Form
                    jobId={id}
                    onSubmitDone={() => {
                      if (typeof window !== "undefined") {
                        window.location.href = `/jobs/${url_slug}/application-complete`;
                      }
                    }}
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} md={4}>
            <Stack gap={4}>
              <CTA
                className={clsx(classes.content__details__cta, 'd-none d-md-block')}
                label="Apply now"
                href="#Apply"
                variant="secondary"
              />
              {(consultant || branch) && (employer?.name !== "TRC") && (
                <Card>
                  <Card.Header>
                    <h3>Have a Question?</h3>
                  </Card.Header>
                  <Card.Body>
                    <Phone
                      className="mb-0"
                      number={consultant?.phone_number || branch?.phone || global["_theme.company.phone"]}
                    />
                  </Card.Body>
                </Card>
              )}
              {testimony && (
                <Card>
                  <Card.Header className="bg-tertiary">
                    <h3>What candidates say</h3>
                  </Card.Header>
                  <Card.Body>
                    <StarRating value={testimony.rating} />
                    <blockquote>
                      <svg width="35" height="25" viewBox="0 0 35 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M12.5833 0.208313L7.66663 10.0416H15.0416V24.7916H0.291626V10.0416L5.20829 0.208313H12.5833ZM32.25 0.208313L27.3333 10.0416H34.7083V24.7916H19.9583V10.0416L24.875 0.208313H32.25Z"
                          fill="#009EE3"
                        />
                      </svg>{" "}
                      <span dangerouslySetInnerHTML={{ __html: stripTags(testimony.quote) }} />
                    </blockquote>
                  </Card.Body>
                </Card>
              )}
              <SocialShare title="Share this job" />
              <RelatedJobs items={related} />
            </Stack>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

JobPageContent.defaultProps = {
  className: "pb-4",
  description: null,
  img: null,
  location: null,
  salary_package: null,
  external_reference: null,
  published_at: null,
};

JobPageContent.propTypes = {
  className: PropTypes.string,
  description: PropTypes.string,
  img: PropTypes.string,
  location: PropTypes.string,
  salary_package: PropTypes.string,
  external_reference: PropTypes.string,
  published_at: PropTypes.string,
};
